import { useContext, useState } from "react";
import { Box, Card, CardContent, CardMedia, Button, Container, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Stack, Typography, IconButton, Chip } from "@mui/material";
import newsBG from "../../../assets/images/newsBG.jpg";
import { useTranslation } from "react-i18next";
import { chooseButtonTurquoiseStyle } from "../../../shared/styles/buttonStyles";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import CloseIcon from "@mui/icons-material/Close";
import filterContext from "../../../app/providers/filter/filterContext";
import { useNavigate } from "react-router-dom";

const newsData = [
  {
    id: 1,
    title: "Vaihteiston kuntotarkastus",
    image: "https://images.unsplash.com/photo-1569313888792-5483b9b43fc0?w=900",
    caption: "Vaihteiston tarkastus",
    shortDescription: "Valmistele ajoneuvosi talvikauteen erityisellä vaihteiston tarkastus- ja vaihtokampanjallamme.",
    content:
      "Talvikampanjaamme kuuluu vaihteiston täydellinen tarkastus, paineen tarkistus, kulutuspinnan syvyyden mittaus, tasapainotus ja ammattimainen asennus. Kuukauden loppuun mennessä ajan varaavat asiakkaat saavat lisäksi veloituksettoman ajoneuvon turvallisuustarkastuksen. Kampanja varmistaa, että ajoneuvosi vaihteisto on parhaassa mahdollisessa kunnossa tulevaa talvikautta varten. Varaa aikasi jo tänään!",
    service: {
      fuelType: "all",
      level: "extra",
      filterable: true,
      headerName: "availableServices",
      category: "maintenance",
      translations: {
        en: {
          name: "Manual Gearbox Repair",
          description: "Maintenance and repair of manual transmissions.",
        },
        fi: {
          name: "Manuaalivaihteiston korjaus",
          description: "Manuaalivaihteistojen huolto ja korjaustyöt.",
        },
        sv: {
          name: "Manuell växellådsreparation",
          description: "Service och reparation av manuella växellådor.",
        },
      },
      type: "base",
      id: "06029472-718d-4d86-aad0-62c3cb6efbda",
      active: false,
    },
    createdAt: "07/08/2023",
  },
  {
    id: 2,
    title: "Maalipinnan suojaus",
    image: "https://images.unsplash.com/photo-1594097840961-411cf23dd23f?w=900",
    caption: "Maalipinnan suojaus",
    shortDescription: "Sertifioidut teknikkomme tarjoavat nyt kattavia maalipinnan suojauspalveluja, jotka pitävät ajoneuvosi uudenveroisena.",
    content:
      "Maalipinnan suojauspalveluun kuuluu perusteellinen pesu, savikäsittely, maalipinnan korjaus ja korkealaatuisen keraamisen pinnoitteen levitys. Tämä monivaiheinen prosessi suojaa ajoneuvosi maalipintaa ympäristön epäpuhtauksilta, UV-säteilyltä ja pieniltä naarmuilta. Maalipinnan suojaus on tärkeää ajoneuvon ulkonäön ja arvon säilyttämiseksi. Varaa aikasi jo tänään!",
    service: {
      fuelType: "all",
      level: "extra",
      filterable: true,
      headerName: "availableServices",
      category: "maintenance",
      id: "5c5140be-a2d2-42d0-90a3-1b932684d30b",
      translations: {
        en: {
          name: "Car Painting",
          description: "Painting and refinishing of vehicle surfaces.",
        },
        fi: {
          name: "Automaalaus",
          description: "Ajoneuvon pintavaurioiden maalaus ja värikorjaukset.",
        },
        sv: {
          name: "Billackering",
          description: "Lackering och ytbehandling av fordon.",
        },
      },
      type: "base",
      active: false,
    },
    createdAt: "07/08/2023",
  },
  {
    id: 3,
    title: "Aviloo-akun kuntotarkastus",
    image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=900",
    caption: "Akun kuntotarkastus",
    shortDescription: "Varmista sähköautosi paras suorituskyky Aviloo-akun kuntotarkastuksella.",
    content:
      "Aviloo-akun kuntotarkastus sisältää kattavan analyysin akun suorituskyvystä, kapasiteetista ja yleisestä kunnosta. Teknikkomme käyttävät edistyneitä diagnostiikkatyökaluja mahdollisten ongelmien tunnistamiseen ja toimittavat sinulle yksityiskohtaisen raportin. Aviloon innovatiivinen teknologia takaa tarkat tulokset ja mielenrauhan sähköauton omistajille. Varaa aikasi jo tänään!",
    service: {
      translations: {
        en: {
          name: "Aviloo Test",
          description: "Battery health test for electric and hybrid vehicles.",
        },
        fi: {
          name: "Aviloo-testi",
          description: "Sähkö- ja hybridiauton akun kunnon testaus.",
        },
        sv: {
          name: "Aviloo-test",
          description: "Batterihälsotest för el- och hybridfordon.",
        },
      },
      id: "15aa7550-0286-4c1c-a8c1-5d9a718cbfc7",
      level: "extra",
      filterable: true,
      fuelType: "electricity",
      headerName: "availableServices",
      category: "maintenance",
      type: "base",
      active: false,
    },
    createdAt: "07/08/2023",
  },
];

const WorkshopNewsSection = () => {
  const { t, i18n } = useTranslation();
  const { isMobile, isTablet } = useWindowSize();
  const [selectedNews, setSelectedNews] = useState(null);
  const { setSelectedService } = useContext(filterContext);
  const navigate = useNavigate();

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    navigate("/workshoplist");
  };

  return (
    <>
      <Box sx={{ position: "relative", py: { xs: 6, md: 10 }, overflow: "hidden" }}>
        {/* Background Image */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${newsBG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.5,
            zIndex: 0,
          }}
        />

        <Container maxWidth='lg' sx={{ position: "relative", zIndex: 1 }}>
          <Stack spacing={1} alignItems='center' mb={6}>
            <Typography className='accent-color-dark' variant={isMobile ? "h4" : isTablet ? "h3" : "h2"} component='h2' sx={{ textAlign: "center", fontWeight: "bold", marginBottom: 2 }}>
              {t("workshopNews.headerText")}
            </Typography>

            <Typography className='accent-color-dark' variant='body1' component='p' sx={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
              {t("workshopNews.subHeaderText")}
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {newsData.map((news) => (
              <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }} key={news.id}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia component='img' image={news.image} height='220' alt={news.title} />

                    <Chip
                      label={news.service.translations[i18n.language]?.name || news.service.translations.fi.name}
                      size='small'
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        fontWeight: 600,
                        bgcolor: "#003C59",
                        color: "#fff",
                        boxShadow: 2,
                      }}
                    />
                  </Box>

                  <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <Typography variant='h6' fontWeight={700} gutterBottom>
                      {news.title}
                    </Typography>

                    <Typography variant='body2' color='text.secondary' sx={{ flexGrow: 1 }}>
                      {news.shortDescription.length > 200 ? `${news.shortDescription.substring(0, 200)}...` : news.shortDescription}
                    </Typography>

                    <Button variant='contained' sx={{ ...chooseButtonTurquoiseStyle, mt: 3, alignSelf: "flex-start" }} onClick={() => setSelectedNews(news)}>
                      {t("workshopNews.readMore")}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Dialog */}

      <Dialog open={Boolean(selectedNews)} onClose={() => setSelectedNews(null)} maxWidth='md' fullWidth>
        {selectedNews && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: isMobile ? "flex-end" : "space-between", alignItems: isMobile ? "flex-start" : "center", height: isMobile ? "50px" : "auto" }}>
              {!isMobile && (
                <Box>
                  <Typography variant='h6' sx={{ color: "#003C59", fontWeight: "bold" }}>
                    {selectedNews.title}
                  </Typography>
                  <Typography variant='body2'>{selectedNews.shortDescription}</Typography>
                </Box>
              )}
              <IconButton onClick={() => setSelectedNews(null)} sx={{ color: "#003C59", position: isMobile ? "absolute" : "static", top: isMobile ? 8 : "auto", right: isMobile ? 8 : "auto" }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Box>
                {isMobile && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='h6' sx={{ color: "#003C59", fontWeight: "bold" }}>
                      {selectedNews.title}
                    </Typography>
                    <Typography variant='body2' sx={{ color: "#666", textAlign: "justify" }}>
                      {selectedNews.shortDescription}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ float: "left", display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: "column" }}>
                  <Box
                    component='img'
                    src={selectedNews.image}
                    alt={selectedNews.title}
                    sx={{
                      width: { xs: "100%", md: 320 },
                      objectFit: "cover",
                      borderRadius: 2,
                      mr: { md: 3 },
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant='caption'
                    sx={{
                      display: "block",
                      fontStyle: "italic",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    {selectedNews.caption}
                  </Typography>
                </Box>

                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.5,
                    textAlign: "justify",
                    overflowWrap: "break-word",
                    wordBreak: "normal",
                    hyphens: "auto",
                  }}
                >
                  {selectedNews.content}
                  <Typography component={'span'} sx={{ fontSize: "0.8rem", color: "#aaa", fontStyle: "italic" }}>&nbsp;{`posted on ${selectedNews.createdAt}`}</Typography>
                </Typography>
                <Box sx={{ clear: "both" }} />
              </Box>
            </DialogContent>

            <DialogActions>
              <Typography variant='body2' sx={{ fontWeight: 600, color: "#003C59" }}>
                {t("workshopNews.forService")} {` ${selectedNews.service.translations[i18n.language]?.name || selectedNews.service.translations.fi.name}`}
              </Typography>
              <Button onClick={() => handleServiceSelect(selectedNews.service.id)} color='primary' variant='outlined'>
                {t("workshopNews.findWorkshop")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default WorkshopNewsSection;
